import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";

/**
 * Reusable payment-creation modal.
 *
 * Props:
 * - isOpen, toggle: modal control
 * - type: 'HOTEL_RESERVATION' | 'RESERVATION' | 'VISA'
 * - linkId: optional foreign key id (hotelReservationId / reservationId / visaId)
 * - prefill: { firstname, lastname, phonenumber, email, amount } (all optional)
 * - sourceLabel: short string shown in the header (e.g. "Réservation #123")
 * - onSubmit: async (paymentPayload) => void  — called after validation
 * - submitting: boolean — disables the submit button
 */
const TYPE_META = {
  HOTEL_RESERVATION: { label: "Réservation hôtel", color: "primary", icon: "bx-buildings" },
  RESERVATION:       { label: "Voyage organisé",   color: "info",    icon: "bx-plane-take-off" },
  VISA:              { label: "Visa",               color: "warning", icon: "bx-id-card" },
};

const PaymentFormModal = ({
  isOpen,
  toggle,
  type,
  linkId,
  prefill = {},
  sourceLabel,
  onSubmit,
  submitting = false,
}) => {
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "",
    email: "",
    amount: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  // Reset/prefill whenever the modal opens (or the prefill payload changes
  // — listing every primitive keeps eslint happy without depending on
  // object identity, which would re-fire on every parent render).
  useEffect(() => {
    if (isOpen) {
      setForm({
        firstname: prefill.firstname || "",
        lastname: prefill.lastname || "",
        phonenumber: prefill.phonenumber || "",
        email: prefill.email || "",
        amount:
          prefill.amount != null && prefill.amount !== ""
            ? String(prefill.amount)
            : "",
        notes: "",
      });
      setErrors({});
    }
  }, [
    isOpen,
    prefill.firstname,
    prefill.lastname,
    prefill.phonenumber,
    prefill.email,
    prefill.amount,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = "Le prénom est requis";
    if (!form.lastname.trim()) e.lastname = "Le nom est requis";
    if (!form.phonenumber.trim()) {
      e.phonenumber = "Le numéro de téléphone est requis";
    } else if (!/^\+?[0-9]{8,14}$/.test(form.phonenumber.trim())) {
      e.phonenumber = "Numéro de téléphone invalide";
    }
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "E-mail invalide";
    }
    const amt = Number(form.amount);
    if (!form.amount || Number.isNaN(amt) || amt <= 0) {
      e.amount = "Montant invalide";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      type,
      firstname: form.firstname.trim(),
      lastname: form.lastname.trim(),
      phonenumber: form.phonenumber.trim(),
      email: form.email.trim() || undefined,
      amount: Number(form.amount),
      notes: form.notes.trim() || undefined,
      // Attach the appropriate FK based on type
      ...(type === "HOTEL_RESERVATION" && linkId ? { hotelReservationId: linkId } : {}),
      ...(type === "RESERVATION" && linkId ? { reservationId: linkId } : {}),
      ...(type === "VISA" && linkId ? { visaId: linkId } : {}),
    };

    await onSubmit(payload);
  };

  const meta = TYPE_META[type] || TYPE_META.RESERVATION;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        <i className={`bx ${meta.icon} me-2`} />
        Enregistrer un paiement
      </ModalHeader>
      <ModalBody>
        <div className="d-flex align-items-center gap-2 mb-4">
          <Badge color={meta.color} className="font-size-12">
            {meta.label}
          </Badge>
          {sourceLabel && (
            <small className="text-muted">{sourceLabel}</small>
          )}
        </div>

        <Form onSubmit={handleSubmit} id="payment-form">
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Prénom</Label>
                <Input
                  name="firstname"
                  value={form.firstname}
                  onChange={handleChange}
                  invalid={!!errors.firstname}
                />
                {errors.firstname && <FormFeedback>{errors.firstname}</FormFeedback>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Nom</Label>
                <Input
                  name="lastname"
                  value={form.lastname}
                  onChange={handleChange}
                  invalid={!!errors.lastname}
                />
                {errors.lastname && <FormFeedback>{errors.lastname}</FormFeedback>}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  name="phonenumber"
                  value={form.phonenumber}
                  onChange={handleChange}
                  invalid={!!errors.phonenumber}
                  placeholder="0555123456"
                />
                {errors.phonenumber && <FormFeedback>{errors.phonenumber}</FormFeedback>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>E-mail (facultatif)</Label>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  invalid={!!errors.email}
                />
                {errors.email && <FormFeedback>{errors.email}</FormFeedback>}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Montant (DA)</Label>
                <Input
                  type="number"
                  name="amount"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={handleChange}
                  invalid={!!errors.amount}
                />
                {errors.amount && <FormFeedback>{errors.amount}</FormFeedback>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Type de paiement</Label>
                <Input value={meta.label} readOnly disabled />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Notes (facultatif)</Label>
            <Input
              type="textarea"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Remarques internes, mode de paiement, référence…"
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          Annuler
        </Button>
        <Button color="primary" type="submit" form="payment-form" disabled={submitting}>
          {submitting ? "Enregistrement…" : "Enregistrer le paiement"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PaymentFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["HOTEL_RESERVATION", "RESERVATION", "VISA"]).isRequired,
  linkId: PropTypes.string,
  prefill: PropTypes.object,
  sourceLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

export default PaymentFormModal;
